import { collection, doc, getDocs, query, setDoc, Timestamp, where } from 'firebase/firestore';
import type { Template, TemplateType } from '../types/template';
import { firestore } from './firebase';
import { httpClient } from './http/httpClient';
import storageService from './storageService';

/**
 * Fetches all templates for a given user, optionally filtered by type.
 */
export const getUserTemplates = async (
  userId: string,
  type?: TemplateType
): Promise<Template[]> => {
  const templatesRef = collection(firestore, 'users', userId, 'templates');
  let q = query(templatesRef);

  if (type) {
    q = query(templatesRef, where('type', '==', type));
  }

  const querySnapshot = await getDocs(q);
  const templates: Template[] = [];

  for (const doc of querySnapshot.docs) {
    const data = doc.data();
    templates.push({
      id: doc.id,
      ...data,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt : data.updatedAt,
      scope: 'personal',
    } as Template);
  }

  return templates;
};

/**
 * Fetches all templates for a given client, optionally filtered by type.
 */
export const getClientTemplates = async (
  clientId: string,
  type?: TemplateType
): Promise<Template[]> => {
  const templatesRef = collection(firestore, 'clients', clientId, 'templates');
  let q = query(templatesRef);

  if (type) {
    q = query(templatesRef, where('type', '==', type));
  }

  const querySnapshot = await getDocs(q);
  const templates: Template[] = [];

  for (const doc of querySnapshot.docs) {
    const data = doc.data();
    templates.push({
      id: doc.id,
      ...data,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt : data.updatedAt,
      scope: 'company',
    } as Template);
  }

  return templates;
};

/**
 * Fetches all templates available to the user (User + Client).
 */
export const getAllTemplates = async (
  userId: string,
  clientId: string,
  type?: TemplateType
): Promise<Template[]> => {
  const [userTemplates, clientTemplates] = await Promise.all([
    getUserTemplates(userId, type),
    getClientTemplates(clientId, type),
  ]);

  // Merge, maybe prioritizing user templates or just concatenating
  return [...clientTemplates, ...userTemplates];
};

/**
 * Uploads a new template file to Storage via Cloud Function and saves metadata to Firestore.
 */
export const uploadUserTemplate = async (
  userId: string,
  clientId: string,
  teamId: string,
  file: File,
  type: TemplateType,
  validTags: string[]
): Promise<Template> => {
  return uploadTemplate({
    userId,
    clientId,
    teamId,
    file,
    type,
    validTags,
    scope: 'personal',
  });
};

/**
 * Uploads a client-wide template.
 */
export const uploadClientTemplate = async (
  userId: string,
  clientId: string,
  file: File,
  type: TemplateType,
  validTags: string[]
): Promise<Template> => {
  return uploadTemplate({
    userId,
    clientId,
    teamId: 'client_admin', // Not used for client scope, but required by type signature or backend
    file,
    type,
    validTags,
    scope: 'company',
  });
};

interface UploadTemplateParams {
  userId: string;
  clientId: string;
  teamId: string;
  file: File;
  type: TemplateType;
  validTags: string[];
  scope: 'personal' | 'company';
}

const uploadTemplate = async ({
  userId,
  clientId,
  teamId,
  file,
  type,
  validTags,
  scope,
}: UploadTemplateParams): Promise<Template> => {
  const templateId = crypto.randomUUID();
  // Use templateId as filename to ensure uniqueness and match expected pattern
  const fileName = `${templateId}.docx`;

  const bucketName =
    import.meta.env.VITE_STORAGE_BUCKET || import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;

  if (!bucketName) {
    throw new Error('Storage bucket not configured in environment variables');
  }

  interface UploadResponse {
    success: boolean;
    storagePath: string;
    fileName: string;
    message?: string;
  }

  const keyHeader = '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/octet-stream',
    'X-User-ID': userId,
    'X-Client-ID': clientId,
    'X-Team-ID': teamId,
    'X-Bucket-Name': bucketName,
    'X-File-Type': type,
    'X-File-Name': fileName,
    'X-Scope': scope,
    'X-File-Mime-Type':
      file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };

  // Always add encryption key if available (now it might be Client Key or User Key)
  if (keyHeader) {
    headers['X-Encryption-Key'] = keyHeader;
  }

  const response = await httpClient.post<UploadResponse>('/uploadUserTemplate', file, {
    headers,
  });

  if (!response.success || !response.storagePath) {
    throw new Error(response.message || 'Template upload failed');
  }

  const storagePath = response.storagePath;

  // 2. Create Firestore Document
  const templateData: Template = {
    id: templateId,
    name: file.name, // Display name
    originalFilename: file.name,
    type,
    storagePath,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    ownerId: userId,
    clientId: clientId,
    isDefault: false,
    isTeamDefault: false,
    validTagsFound: validTags,
    scope: scope,
  };

  const collectionPath =
    scope === 'company' ? `clients/${clientId}/templates` : `users/${userId}/templates`;

  await setDoc(doc(firestore, collectionPath, templateId), templateData);

  return templateData;
};

/**
 * Deletes a template from Firestore and Storage via Cloud Function.
 */
export const deleteUserTemplate = async (_userId: string, template: Template): Promise<void> => {
  await httpClient.post('/manageUserTemplates', {
    action: 'delete',
    templateId: template.id,
    storagePath: template.storagePath,
  });
};

/**
 * Deletes a client template.
 */
export const deleteClientTemplate = async (template: Template): Promise<void> => {
  await httpClient.post('/manageUserTemplates', {
    action: 'delete',
    templateId: template.id,
    storagePath: template.storagePath,
    scope: 'company',
  });
};

/**
 * Sets a template as the default for its type, either for the User (Level 2) or the Team (Level 3).
 *
 * @param scope 'personal' | 'team' | 'company'
 */
export const setTemplateAsDefault = async (
  _userId: string,
  _clientId: string,
  _teamId: string,
  template: Template,
  scope: 'personal' | 'team' | 'company'
): Promise<void> => {
  // Delegate to Cloud Function to ensure atomicity and avoid CORS issues with Storage Copy
  await httpClient.post('/manageUserTemplates', {
    action: 'setDefault',
    templateId: template.id,
    scope,
  });
};

/**
 * Downloads a template file.
 * Tries direct storage fetch (for Firebase), then falls back to signed URL (for SharePoint).
 */
export const downloadTemplate = async (template: Template): Promise<Blob> => {
  try {
    // 1. Try Direct Storage Fetch (StorageService)
    if (storageService.getStorageType() === 'firebase') {
      return await storageService.downloadAndDecryptFile(template.storagePath);
    }
  } catch (_error) {
    // Ignore direct fetch error, fallback to cloud function
  }

  // 2. Fallback: Get Download URL via Cloud Function
  // This works for SharePoint (unencrypted) or if client-side access is restricted.
  const url = await getTemplateDownloadUrl(template.storagePath);

  // Fetch the content
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }

  return await response.blob();
};

/**
 * Gets the download URL for a template via Cloud Function (supports SharePoint).
 */
export const getTemplateDownloadUrl = async (storagePath: string): Promise<string> => {
  const response = await httpClient.post<{ success: boolean; url: string }>(
    '/manageUserTemplates',
    {
      action: 'getDownloadUrl',
      storagePath,
    }
  );
  // httpClient.post returns the response body data directly (T) if configured, or we access .data
  // Assuming existing pattern returns parsed JSON
  const result = response as unknown as { success: boolean; url: string };

  if (result.success && result.url) {
    return result.url;
  }
  throw new Error('Failed to get download URL');
};

/**
 * Fetches the template content (base64) for previewing.
 * Uses Cloud Function to bypass CORS issues.
 */
export const getTemplatePreviewContent = async (storagePath: string): Promise<string> => {
  const bucketName =
    import.meta.env.VITE_STORAGE_BUCKET || import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;

  const response = await httpClient.post<{ success: boolean; data: string }>(
    '/manageUserTemplates',
    {
      action: 'getPreview',
      storagePath,
      bucketName,
    }
  );
  // httpClient.post returns the response body data directly (T)
  const result = response as unknown as { success: boolean; data: string };
  return result.data;
};

/**
 * Sets a specific template for a given tender.
 * Copies the template file to the tender's storage location and updates the tender document.
 */
export const setTenderTemplate = async (
  tenderId: string,
  templateId: string,
  type: string
): Promise<void> => {
  await httpClient.post('/manageUserTemplates', {
    action: 'setTenderTemplate',
    tenderId,
    templateId,
    type,
  });
};

/**
 * Clears the specific template selection for a given tender, reverting to defaults.
 * Deletes the tender-specific template file and removes the reference from the tender document.
 */
export const clearTenderTemplate = async (tenderId: string, type: string): Promise<void> => {
  await httpClient.post('/manageUserTemplates', {
    action: 'clearTenderTemplate',
    tenderId,
    type,
  });
};
